import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useStableId from '../../services/useStableId';
import VISUALLY_HIDDEN_SX from '../visuallyHiddenSx';
import { pickForHighContrast } from '../highContrastSx';
import { createFieldFocusHalo } from '../focusRingSx';
import FIELD_SURFACE_SX from './fieldSurfaceSx';

function fieldFocusShadow(theme) {
    return createFieldFocusHalo(theme, {
        color: theme.palette.primary.main,
        haloOpacity: 0.15,
    });
}

const FIELD_CONTROL_FOCUS_SX = pickForHighContrast({ outline: 'none', boxShadow: 'none' }, null);

const FIELD_BOX_BASE_SX = {
    ...FIELD_SURFACE_SX,
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    px: { xs: 1.5, md: 1.25 },
    py: { xs: 1.25, md: 1 },
    minHeight: { xs: 42, md: 38 },
    transition: 'background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease',
    '&:focus-within': {
        bgcolor: 'background.default',
        borderColor: 'primary.main',
        boxShadow: fieldFocusShadow,
    },
    '& > :focus-visible': FIELD_CONTROL_FOCUS_SX,
};

const FIELD_BOX_ACTIVE_SX = {
    bgcolor: 'background.default',
    borderColor: 'primary.main',
    boxShadow: fieldFocusShadow,
};

const LABEL_SX = {
    color: 'grey.500',
    mb: { xs: 0.75, md: 0.5 },
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};

const ICON_SX = { color: 'grey.400', display: 'flex', flexShrink: 0 };

function LabeledField({
    label, icon = null, active = false, hideLabel = false, renderInput,
}) {
    const inputId = useStableId('search-panel-field');

    return (
        <Box sx={{
 display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%',
}}
        >
            <Typography
                component="label"
                htmlFor={inputId}
                variant="labelS"
                sx={hideLabel ? VISUALLY_HIDDEN_SX : LABEL_SX}
            >
                {label}
            </Typography>
            <Box sx={active ? { ...FIELD_BOX_BASE_SX, ...FIELD_BOX_ACTIVE_SX } : FIELD_BOX_BASE_SX}>
                {icon ? <Box sx={ICON_SX} aria-hidden="true">{icon}</Box> : null}
                {renderInput({ id: inputId })}
            </Box>
        </Box>
    );
}

LabeledField.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.node,
    active: PropTypes.bool,
    hideLabel: PropTypes.bool,
    renderInput: PropTypes.func.isRequired,
};

export default LabeledField;
